import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Grid, Icon, Input, Modal } from 'semantic-ui-react';
import { SaveQuotationModalProps, FormProps } from './types';
import {
  Container,
  Header,
  Line,
  FormContainer,
  FormTitle,
  Form,
  DisclaimerContainer,
  Disclaimer,
  ButtonContainer,
  QuotationContainer,
  QuotationHeader,
  QuotationResultContainer,
  QuotationList,
  QuotationValue,
  QuotationListItem
} from './styles';
import CustomDatePicker from '../../../../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../../../../components/MaskedInput';
import { formatAmount } from '../../../../../../../services/masks';
import { warningNotification } from '../../../../../../../services/notification';
import { iRootState } from '../../../../../../../store';
import LenderInsuranceAddon from '../../../../../../../components/LenderInsuranceAddon';
import { useSelector, connect } from 'react-redux';
import TenantForm from './forms/SaveQuotationModalTenantForm';
import OwnerForm from './forms/SaveQuotationModalOwnerForm';
import PropertyForm from './forms/SaveQuotationModalPropertyForm';
import {
  cnpjValidity,
  cpfValidity,
  isBackofficeTestUser
} from '../../../../../../../util';
import FirePropertyWalletSearchDropdown, {
  FireWalletSelection
} from '../../../../components/FirePropertyWalletSearchDropdown';

const mapState = (state: iRootState) => ({
  user: state.user
});

const SaveQuotationModal: React.FC<
  SaveQuotationModalProps & ReturnType<typeof mapState>
> = ({
  isOpen,
  onClose,
  handleSubmit,
  quotation,
  quote,
  user,
  isHiring,
  occupation,
  previousData,
  estateId
}: SaveQuotationModalProps & ReturnType<typeof mapState>) => {
    const isBackofficeTest = isBackofficeTestUser(user);
    const [form, setForm] = useState<FormProps>({
      tenantIdentification: {
        name: previousData?.tenant?.name,
        document: previousData?.tenant?.document,
        birthdate: previousData?.tenant?.birthdate
          ? new Date(previousData?.tenant?.birthdate)
          : undefined,
        email: previousData?.tenant?.email,
        phoneNumber: previousData?.tenant?.phoneNumber
      },
      ownerIdentification: {
        name: previousData?.insured?.name,
        document: previousData?.insured?.document,
        email: previousData?.insured?.email
      },
      propertyAddress: {
        zipCode: previousData?.address?.zipCode,
        street: previousData?.address?.street,
        city: previousData?.address?.city,
        state: previousData?.address?.state,
        neighborhood: previousData?.address?.neighborhood,
        number: previousData?.address?.number,
        complement:
          previousData?.address?.complement ||
          (previousData?.address?.zipCode ? 'SEM COMPLEMENTO' : undefined),
        noComplement: !previousData?.address?.complement && !!previousData?.address?.zipCode
      },
      quotation: {
        rentValue: previousData?.quotation?.rentValue,
        vigency: previousData?.quotation?.vigency,
        startDate: previousData?.quotation?.startDate,
        selectedInstallment: previousData?.quotation?.selectedInstallment,
        totalValue: previousData?.quotation?.totalValue
      }
    });
    const [validateForm, setValidateForm] = useState<boolean>(false);
    const [isLoadingCEP, setIsLoadingCEP] = useState<boolean>(false);
    const [walletSelection, setWalletSelection] = useState<FireWalletSelection | null>(null);

    const lockedFromWallet = Boolean(walletSelection?.propertyPublicId);

    const handleWalletSelect = (selection: FireWalletSelection | null) => {
      setWalletSelection(selection);

      if (!selection) return;

      const { address } = selection;
      setForm(prevValue => ({
        ...prevValue,
        propertyAddress: {
          zipCode: address.zipCode || '',
          street: address.street || '',
          city: address.city || '',
          state: address.state || '',
          neighborhood: address.neighborhood || '',
          number: address.number || '',
          complement: address.complement || 'SEM COMPLEMENTO',
          noComplement: !address.complement
        }
      }));
    };

    useEffect(() => {
      if (!isOpen) {
        setWalletSelection(null);
        return;
      }

      if (!previousData?.address) return;

      setForm(prevValue => ({
        ...prevValue,
        propertyAddress: {
          zipCode: previousData.address.zipCode || '',
          street: previousData.address.street || '',
          city: previousData.address.city || '',
          state: previousData.address.state || '',
          neighborhood: previousData.address.neighborhood || '',
          number: previousData.address.number || '',
          complement:
            previousData.address.complement ||
            (previousData.address.zipCode ? 'SEM COMPLEMENTO' : ''),
          noComplement:
            !previousData.address.complement && Boolean(previousData.address.zipCode)
        }
      }));
    }, [isOpen, previousData?.address]);

    const lenderInsuranceEnabled = useSelector(
      (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
    );
    const lenderInsuranceRate = useSelector(
      (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceRate
    );
    const [includeLenderInsurance, setIncludeLenderInsurance] = useState<boolean>(false);

    const getFormIsValid = () => {
      let isValid = true;

      const requiredTenantFields = [
        { field: form.tenantIdentification.name, name: 'Nome do inquilino' },
        { field: form.tenantIdentification.document, name: 'Documento do inquilino' },
        { field: form.tenantIdentification.birthdate, name: 'Data de nascimento do inquilino' },
        { field: form.tenantIdentification.email, name: 'Email do inquilino' },
        { field: form.tenantIdentification.phoneNumber, name: 'Telefone do inquilino' }
      ];

      requiredTenantFields.forEach(({ field, name }) => {
        if (!field || (typeof field === 'string' && field.trim() === '')) {
          console.log("DEBUG", `Campo obrigatório vazio: ${name}`);
          isValid = false;
        }
      });

      const requiredOwnerFields = [
        { field: form.ownerIdentification.name, name: 'Nome do proprietário' },
        { field: form.ownerIdentification.document, name: 'Documento do proprietário' },
        { field: form.ownerIdentification.email, name: 'Email do proprietário' }
      ];

      requiredOwnerFields.forEach(({ field, name }) => {
        if (!field || (typeof field === 'string' && field.trim() === '')) {
          console.log("DEBUG", `Campo obrigatório vazio: ${name}`);
          isValid = false;
        }
      });

      const requiredAddressFields = [
        { field: form.propertyAddress.zipCode, name: 'CEP' },
        { field: form.propertyAddress.street, name: 'Rua' },
        { field: form.propertyAddress.city, name: 'Cidade' },
        { field: form.propertyAddress.state, name: 'Estado' },
        { field: form.propertyAddress.neighborhood, name: 'Bairro' },
        { field: form.propertyAddress.number, name: 'Número' }
      ];

      requiredAddressFields.forEach(({ field, name }) => {
        if (!field || (typeof field === 'string' && field.trim() === '')) {
          console.log("DEBUG", `Campo obrigatório vazio: ${name}`);
          isValid = false;
        }
      });

      // Validação do complemento (condicional)
      if (!form.propertyAddress.noComplement && (!form.propertyAddress.complement || form.propertyAddress.complement.trim() === '')) {
        console.log("DEBUG", "Complemento obrigatório não preenchido");
        isValid = false;
      }

      if (occupation === 645 && !form.propertyAddress.complement) {
        isValid = false;
        warningNotification(
          'Atenção',
          'Para Apartamento, o complemento do endereço é obrigatório!'
        );
        form.propertyAddress.noComplement = false;
      }

      if (isNaN(Number(form.propertyAddress.number))) {
        isValid = false;
        warningNotification('Atenção', 'O Número do Endereço deve conter apenas números.');
      }

      if (!form.propertyAddress.noComplement && !form.propertyAddress.complement) {
        isValid = false;
        warningNotification('Atenção', 'O complemento do endereço é obrigatório.');
      }

      if (
        !cpfValidity(form.tenantIdentification.document) &&
        !cnpjValidity(form.tenantIdentification.document)
      ) {
        isValid = false;
        warningNotification('Atenção', 'Documento do inquilino inválido.');
      }

      if (
        !cpfValidity(form.ownerIdentification.document) &&
        !cnpjValidity(form.ownerIdentification.document)
      ) {
        isValid = false;
        warningNotification('Atenção', 'Documento do proprietário inválido.');
      }

      return isValid;
    };

    useEffect(() => {
      if (quotation) {
        setForm(prevValue => ({
          ...prevValue,
          quotation
        }));
      }
    }, [quotation]);

    const monthsOptions = () => {
      return Array.from(Array(12).keys()).map(month => {
        return {
          key: month + 1,
          text: `${month + 1} ${month >= 1 ? 'Meses' : 'Mês'}`,
          value: month + 1
        };
      });
    };

    if (!isOpen) {
      return null;
    }

    const handleFormSubmit = (e: React.FormEvent<HTMLElement>) => {
      e.preventDefault();
      if (isBackofficeTest) {
        warningNotification(
          'Modo de teste',
          'Este perfil pode simular o orçamento, mas não pode salvá-lo ou contratá-lo.'
        );
        return;
      }
      setValidateForm(true);
      if (getFormIsValid()) {
        let payload = {
          ...form,
          lenderInsurance: includeLenderInsurance,
          propertyPublicId: walletSelection?.propertyPublicId
        };
        if (form.propertyAddress.noComplement) {
          payload = {
            ...payload,
            propertyAddress: {
              ...form.propertyAddress,
              complement: ''
            }
          };
        }
        handleSubmit(payload);
      } else {
        warningNotification(
          'Campos obrigatórios',
          'Por favor, preencha todos os campos obrigatórios.'
        );
      }
    };

    const renderQuotationForm = (): React.ReactNode => {
      return (
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <label>
                VALOR DO ALUGUEL
                <Input
                  placeholder="Valor do Aluguel"
                  fluid
                  disabled
                  value={form?.quotation?.rentValue}
                />
              </label>
            </Grid.Column>
            <Grid.Column width={8}>
              <label>
                DURAÇÃO DA VIGÊNCIA
                <Dropdown
                  fluid
                  search
                  clearable
                  selection
                  disabled
                  placeholder={'Número de Meses'}
                  options={monthsOptions()}
                  value={form?.quotation?.vigency}
                />
              </label>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              <label>
                INÍCIO
                <CustomDatePicker
                  selected={form?.quotation?.startDate ?? undefined}
                  minDate={new Date()}
                  customInput={
                    <AppMaskedInput
                      mask={[
                        /[0-9]/,
                        /[0-9]/,
                        '/',
                        /[0-9]/,
                        /[0-9]/,
                        '/',
                        /[0-9]/,
                        /[0-9]/,
                        /[0-9]/,
                        /[0-9]/
                      ]}
                    />
                  }
                  disabled
                  dateFormat={'dd/MM/yyyy'}
                  style={{
                    marginTop: 0
                  }}
                />
              </label>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    };

    const renderQuotationData = (): React.ReactNode => {
      const selectedInstallment = quote.installments.find(
        item => item.numeroParcela === quote.maxInstallments
      );
      return (
        <QuotationContainer>
          <QuotationHeader>
            <h3>
              <Icon name="shield" /> Resumo do orçamento
            </h3>
          </QuotationHeader>
          <QuotationResultContainer>
            <QuotationList>
              {quote.coverages.map((coverage, index) => (
                <QuotationListItem key={coverage.id} $isOdd={index % 2 === 0}>
                  <div
                    className={`item-name ${!coverage.flagSelecionada ? 'not-included' : ''
                      }`}
                  >
                    {coverage.nomeCobertura}
                    {/*  TODO: improve this logic */}
                    {index === 0 && (
                      <label className="required-message">(Obrigatório)</label>
                    )}
                  </div>
                  {coverage.flagSelecionada && (
                    <div className="value">
                      R$ {formatAmount(coverage.valorPremioTotal)}
                    </div>
                  )}
                  {!coverage.flagSelecionada && (
                    <small className="not-included">Não incluso</small>
                  )}
                </QuotationListItem>
              ))}
            </QuotationList>
            <QuotationValue>
              <h3>Seu orçamento</h3>
              <label>
                <small>{form?.quotation?.selectedInstallment}x</small> R${' '}
                {formatAmount(Number(form?.quotation?.totalValue / form?.quotation?.selectedInstallment || '').toFixed(2))}
              </label>
              <small>Total: R$ {formatAmount(Number(quote.totalValue).toFixed(2))}</small>
            </QuotationValue>
          </QuotationResultContainer>
        </QuotationContainer>
      );
    };

    const renderActionButtons = (): React.ReactNode => {
      return (
        <ButtonContainer>
          <Button type="button" color="red" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            color={isHiring ? 'green' : 'blue'}
            disabled={isBackofficeTest}
          >
            {isHiring ? `Contratar Seguro` : 'Salvar Orçamento'}
          </Button>
        </ButtonContainer>
      );
    };

    return (
      <Modal open={isOpen} onClose={onClose} size={'large'} closeIcon>
        <Container>
          <Header>
            <h3>
              <Icon name="shield" /> {isHiring ? 'Contratar Seguro' : 'Salvar Orçamento'}
            </h3>
          </Header>
          <Line />

          <Modal.Content scrolling>
            {!isHiring && (
              <DisclaimerContainer>
                <Disclaimer>
                  <Icon bordered name="exclamation" />
                  <p>
                    Você pode salvar esse orçamento para contratação posterior. Você terá um
                    prazo de <time>30</time> dias para confirmar a contratação
                  </p>
                </Disclaimer>
              </DisclaimerContainer>
            )}
            <Form onSubmit={handleFormSubmit}>
              <FormContainer>
                <FormTitle>IDENTIFICAÇÃO DO INQUILINO</FormTitle>
                <Line />
                <TenantForm form={form} setForm={setForm} validateForm={validateForm} />
              </FormContainer>
              <FormContainer>
                <FormTitle>IDENTIFICAÇÃO DO PROPRIETÁRIO</FormTitle>
                <Line />
                <OwnerForm form={form} setForm={setForm} validateForm={validateForm} />
              </FormContainer>
              <FormContainer>
                <FormTitle>ENDEREÇO DO IMÓVEL</FormTitle>
                <Line />
                <Grid>
                  <FirePropertyWalletSearchDropdown
                    estateId={estateId}
                    value={walletSelection}
                    onSelect={handleWalletSelect}
                  />
                </Grid>
                <PropertyForm
                  form={form}
                  setForm={setForm}
                  validateForm={validateForm}
                  isLoadingCEP={isLoadingCEP}
                  setIsLoadingCEP={setIsLoadingCEP}
                  user={user}
                  lockedFromWallet={lockedFromWallet}
                />
              </FormContainer>
              <FormContainer>
                <FormTitle>DADOS DA COTAÇÃO</FormTitle>
                <Line />
                {renderQuotationForm()}
              </FormContainer>
              <FormContainer>{renderQuotationData()}</FormContainer>
              {lenderInsuranceEnabled &&
                form.tenantIdentification.document?.replace(/\D/g, '').length === 11 && (
                <div style={{ marginTop: 15, marginBottom: 15 }}>
                  <LenderInsuranceAddon
                    rateValue={lenderInsuranceRate}
                    selected={includeLenderInsurance}
                    onSelect={(selected: boolean) => setIncludeLenderInsurance(selected)}
                  />
                </div>
              )}
              <FormContainer>{renderActionButtons()}</FormContainer>
            </Form>
          </Modal.Content>
        </Container>
      </Modal>
    );
  };

export default connect(mapState)(SaveQuotationModal);
